import { Controller } from "@hotwired/stimulus"
import debounce from "lodash/debounce"

export default class extends Controller {
  static targets = [
    "form",
    "status",
    "lastSaved"
  ]

  static values = {
    url: String,
    delay: Number,
    method: String,
    resourceType: String,
    resourceId: String
  }

  connect() {
    this.delayValue = this.delayValue || 1000 // Default 1 second delay
    this.methodValue = this.methodValue || 'PATCH'
    this.isDirty = false
    this.isSaving = false

    this.setupAutoSave()
    this.setupBeforeUnload()
    this.setupFormSubmitListener()
    this.restoreFormState()
  }

  disconnect() {
    // Cancel any pending saves
    if (this.debouncedSave) {
      this.debouncedSave.cancel()
    }
    
    // Remove beforeunload listener
    window.removeEventListener('beforeunload', this.handleBeforeUnload)
  }

  setupAutoSave() {
    // Create debounced save function
    this.debouncedSave = debounce(this.save.bind(this), this.delayValue)
    
    // Listen for changes on all form inputs
    const form = this.hasFormTarget ? this.formTarget : this.element
    
    // Text inputs, textareas, and selects
    form.querySelectorAll('input, textarea, select').forEach(input => {
      input.addEventListener('input', this.handleChange.bind(this))
      input.addEventListener('change', this.handleChange.bind(this))
    })
    
    // ContentEditable elements
    form.querySelectorAll('[contenteditable="true"]').forEach(element => {
      element.addEventListener('input', this.handleChange.bind(this))
      element.addEventListener('blur', this.handleChange.bind(this))
    })
    
    // Rich text editors (TinyMCE, Trix, etc.)
    this.setupRichTextListeners()
  }

  setupRichTextListeners() {
    // TinyMCE
    if (typeof tinymce !== 'undefined') {
      tinymce.on('addeditor', (e) => {
        e.editor.on('change', () => this.handleChange())
        e.editor.on('keyup', () => this.handleChange())
      })
    }
    
    // Trix
    this.element.addEventListener('trix-change', this.handleChange.bind(this))
    
    // Quill
    if (typeof Quill !== 'undefined') {
      const quillEditors = this.element.querySelectorAll('.quill-editor')
      quillEditors.forEach(editor => {
        const quill = Quill.find(editor)
        if (quill) {
          quill.on('text-change', () => this.handleChange())
        }
      })
    }
  }

  setupBeforeUnload() {
    this.handleBeforeUnload = (event) => {
      if (this.isDirty && !this.isSaving) {
        const message = 'You have unsaved changes. Are you sure you want to leave?'
        event.returnValue = message
        return message
      }
    }

    window.addEventListener('beforeunload', this.handleBeforeUnload)
  }

  setupFormSubmitListener() {
    // Listen for form submission to clear localStorage
    const form = this.hasFormTarget ? this.formTarget : this.element

    // For standard form submission
    form.addEventListener('submit', (event) => {
      // If this is a regular form submission (not AJAX), clear localStorage
      // We'll clear it now as the page will redirect
      if (!event.defaultPrevented) {
        this.clearLocalStorage()
        this.isDirty = false
      }
    })
  }

  handleChange(event) {
    // Mark as dirty
    this.isDirty = true
    
    // Update status to show unsaved changes
    this.updateStatus('unsaved')
    
    // Store form state locally
    this.storeFormState()
    
    // Trigger debounced save
    this.debouncedSave()
    
    // Dispatch custom event
    this.dispatch('change', { 
      detail: { 
        field: event?.target?.name,
        value: event?.target?.value 
      } 
    })
  }

  async save() {
    if (!this.isDirty || this.isSaving) return
    
    this.isSaving = true
    this.updateStatus('saving')
    
    try {
      const formData = this.collectFormData()
      const response = await this.sendSaveRequest(formData)
      
      if (response.ok) {
        this.handleSaveSuccess(response)
      } else {
        await this.handleSaveError(response)
      }
    } catch (error) {
      this.handleNetworkError(error)
    } finally {
      this.isSaving = false
    }
  }

  collectFormData() {
    const form = this.hasFormTarget ? this.formTarget : this.element
    const formData = new FormData(form)
    
    // Add resource info if available
    if (this.resourceTypeValue) {
      formData.append('resource_type', this.resourceTypeValue)
    }
    if (this.resourceIdValue) {
      formData.append('resource_id', this.resourceIdValue)
    }
    
    // Collect contenteditable values
    form.querySelectorAll('[contenteditable="true"][data-field]').forEach(element => {
      formData.append(element.dataset.field, element.innerHTML)
    })
    
    // Collect rich text editor values
    this.collectRichTextData(formData)
    
    return formData
  }

  collectRichTextData(formData) {
    // TinyMCE
    if (typeof tinymce !== 'undefined') {
      tinymce.editors.forEach(editor => {
        const textarea = editor.getElement()
        if (this.element.contains(textarea)) {
          formData.set(textarea.name, editor.getContent())
        }
      })
    }
    
    // Trix
    this.element.querySelectorAll('trix-editor').forEach(editor => {
      const input = editor.editor.element.input
      if (input?.name) {
        formData.set(input.name, editor.editor.element.value)
      }
    })
  }

  async sendSaveRequest(formData) {
    const url = this.urlValue || this.element.dataset.autoSaveUrl
    
    // Convert FormData to JSON for PATCH/PUT requests
    let body
    let headers = {
      'X-CSRF-Token': this.getCSRFToken(),
      'X-Auto-Save': 'true'
    }
    
    if (this.methodValue === 'PATCH' || this.methodValue === 'PUT') {
      const object = {}
      formData.forEach((value, key) => {
        // Handle nested attributes
        if (key.includes('[')) {
          this.setNestedValue(object, key, value)
        } else {
          object[key] = value
        }
      })
      
      body = JSON.stringify(object)
      headers['Content-Type'] = 'application/json'
    } else {
      body = formData
    }
    
    return fetch(url, {
      method: this.methodValue,
      headers: headers,
      body: body
    })
  }

  setNestedValue(object, path, value) {
    const keys = path.match(/[^\[\]]+/g)
    let current = object
    
    keys.forEach((key, index) => {
      if (index === keys.length - 1) {
        current[key] = value
      } else {
        current[key] = current[key] || {}
        current = current[key]
      }
    })
  }

  handleSaveSuccess(response) {
    this.isDirty = false
    this.updateStatus('saved')
    this.updateLastSaved()
    this.clearLocalStorage()
    
    // Dispatch success event
    this.dispatch('save-success', { 
      detail: { 
        response: response,
        timestamp: new Date()
      } 
    })
    
    // Handle JSON response if present
    response.json().then(data => {
      if (data.updated_at) {
        this.element.dataset.updatedAt = data.updated_at
      }
      if (data.version) {
        this.element.dataset.version = data.version
      }
    }).catch(() => {
      // Response might not be JSON
    })
  }

  async handleSaveError(response) {
    const errorText = await response.text()
    let errorMessage = 'Failed to save changes'
    
    try {
      const errorJson = JSON.parse(errorText)
      errorMessage = errorJson.error || errorJson.message || errorMessage
    } catch {
      // Not JSON, use default message
    }
    
    this.updateStatus('error', errorMessage)
    
    // Dispatch error event
    this.dispatch('save-error', { 
      detail: { 
        response: response,
        error: errorMessage 
      } 
    })
    
    // Retry after delay
    setTimeout(() => {
      this.save()
    }, 5000)
  }

  handleNetworkError(error) {
    console.error('Auto-save network error:', error)
    this.updateStatus('offline', 'No connection - will retry when online')
    
    // Listen for online event to retry
    window.addEventListener('online', () => {
      this.save()
    }, { once: true })
  }

  updateStatus(status, message = null) {
    if (!this.hasStatusTarget) return
    
    const statusClasses = {
      'unsaved': 'text-warning',
      'saving': 'text-info animate-pulse',
      'saved': 'text-success',
      'error': 'text-error',
      'offline': 'text-base-content/50'
    }
    
    const statusMessages = {
      'unsaved': 'Unsaved changes',
      'saving': 'Saving...',
      'saved': 'All changes saved',
      'error': message || 'Save failed',
      'offline': message || 'Offline'
    }
    
    // Update classes
    Object.values(statusClasses).forEach(cls => {
      this.statusTarget.classList.remove(...cls.split(' '))
    })
    this.statusTarget.classList.add(...(statusClasses[status] || '').split(' '))
    
    // Update text
    this.statusTarget.textContent = statusMessages[status]
    
    // Add icon
    const icons = {
      'unsaved': '●',
      'saving': '⟳',
      'saved': '✓',
      'error': '✗',
      'offline': '⚠'
    }
    
    this.statusTarget.innerHTML = `
      <span class="mr-1">${icons[status] || ''}</span>
      <span>${statusMessages[status]}</span>
    `
  }

  updateLastSaved() {
    if (!this.hasLastSavedTarget) return
    
    const now = new Date()
    this.lastSavedTarget.textContent = `Last saved at ${now.toLocaleTimeString()}`
    this.lastSavedTarget.dataset.timestamp = now.toISOString()
    
    // Update relative time
    this.startRelativeTimeUpdater()
  }

  startRelativeTimeUpdater() {
    if (this.relativeTimeInterval) {
      clearInterval(this.relativeTimeInterval)
    }
    
    this.relativeTimeInterval = setInterval(() => {
      this.updateRelativeTime()
    }, 60000) // Update every minute
  }

  updateRelativeTime() {
    if (!this.hasLastSavedTarget) return
    
    const timestamp = this.lastSavedTarget.dataset.timestamp
    if (!timestamp) return
    
    const saved = new Date(timestamp)
    const now = new Date()
    const diffMinutes = Math.floor((now - saved) / 60000)
    
    let text
    if (diffMinutes < 1) {
      text = 'Last saved just now'
    } else if (diffMinutes === 1) {
      text = 'Last saved 1 minute ago'
    } else if (diffMinutes < 60) {
      text = `Last saved ${diffMinutes} minutes ago`
    } else {
      const diffHours = Math.floor(diffMinutes / 60)
      text = `Last saved ${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
    }
    
    this.lastSavedTarget.textContent = text
  }

  // Local storage for recovery
  storeFormState() {
    const formData = this.collectFormData()
    const data = {}
    
    formData.forEach((value, key) => {
      data[key] = value
    })
    
    const storageKey = this.getStorageKey()
    localStorage.setItem(storageKey, JSON.stringify({
      data: data,
      timestamp: new Date().toISOString()
    }))
  }

  restoreFormState() {
    const storageKey = this.getStorageKey()
    const stored = localStorage.getItem(storageKey)
    
    if (!stored) return
    
    try {
      const { data, timestamp } = JSON.parse(stored)
      const age = Date.now() - new Date(timestamp).getTime()
      
      // Only restore if less than 24 hours old
      if (age < 24 * 60 * 60 * 1000) {
        this.showRecoveryPrompt(data)
      } else {
        localStorage.removeItem(storageKey)
      }
    } catch (error) {
      console.error('Failed to restore form state:', error)
      localStorage.removeItem(storageKey)
    }
  }

  showRecoveryPrompt(data) {
    const shouldRestore = confirm('You have unsaved changes from a previous session. Would you like to restore them?')
    
    if (shouldRestore) {
      this.restoreData(data)
      this.isDirty = true
      this.updateStatus('unsaved')
    } else {
      this.clearLocalStorage()
    }
  }

  restoreData(data) {
    const form = this.hasFormTarget ? this.formTarget : this.element
    
    Object.entries(data).forEach(([key, value]) => {
      const input = form.querySelector(`[name="${key}"]`)
      if (input) {
        if (input.type === 'checkbox') {
          input.checked = value === 'on' || value === 'true'
        } else if (input.type === 'radio') {
          const radio = form.querySelector(`[name="${key}"][value="${value}"]`)
          if (radio) radio.checked = true
        } else {
          input.value = value
        }
      }
      
      // ContentEditable elements
      const contentEditable = form.querySelector(`[contenteditable="true"][data-field="${key}"]`)
      if (contentEditable) {
        contentEditable.innerHTML = value
      }
    })
  }

  clearLocalStorage() {
    const storageKey = this.getStorageKey()
    localStorage.removeItem(storageKey)
  }

  getStorageKey() {
    const projectId = this.element.dataset.projectId || 'default'
    const resourceType = this.resourceTypeValue || 'form'
    const resourceId = this.resourceIdValue || 'new'
    
    return `autosave_${projectId}_${resourceType}_${resourceId}`
  }

  getCSRFToken() {
    return document.querySelector('[name="csrf-token"]')?.content || ''
  }

  // Manual save trigger
  triggerSave() {
    if (this.debouncedSave) {
      this.debouncedSave.cancel()
    }
    this.save()
  }

  // Force save without debounce
  forceSave() {
    return this.save()
  }

  // Handle form submission end (for Turbo)
  handleSubmitEnd(event) {
    // Check if the submission was successful
    if (event.detail.success) {
      // Clear localStorage for this form
      this.clearLocalStorage()

      // Reset dirty flag
      this.isDirty = false

      // Dispatch event for successful submission
      this.dispatch('form-submitted', {
        detail: {
          success: true,
          formData: event.detail.formData
        }
      })
    }
  }
}